import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const columnsFocusPluginKey = new PluginKey('columnsFocus');

export const Columns = Node.create({
  name: 'columns',
  group: 'block',
  content: 'column+',
  defining: true,
  isolating: true,
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'editor-columns',
        'data-columns': '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-columns]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const parentClass = this.options.HTMLAttributes?.class ?? '';
    const extraClass = HTMLAttributes?.class ?? '';
    const mergedClass = [parentClass, extraClass].filter(Boolean).join(' ');

    return [
      'div',
      { ...this.options.HTMLAttributes, ...HTMLAttributes, class: mergedClass },
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Prevent backspace from joining/modifying columns block
      Backspace: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Only handle if we're at the start of a paragraph
        if ($from.parentOffset !== 0 || !selection.empty) {
          return false;
        }

        // Check if we're in a paragraph
        if ($from.parent.type.name !== 'paragraph') {
          return false;
        }

        // Check the block-level sibling by resolving at block depth
        const $blockPos = state.doc.resolve($from.before($from.depth));
        const nodeBefore = $blockPos.nodeBefore;

        if (!nodeBefore || nodeBefore.type.name !== 'columns') {
          return false;
        }

        // Block the backspace to prevent joining with columns
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: columnsFocusPluginKey,
        props: {
          decorations: state => {
            const { selection } = state;
            const { $from } = selection;
            const decorations: Decoration[] = [];

            for (let depth = $from.depth; depth > 0; depth--) {
              const node = $from.node(depth);
              if (node.type.name === 'columns') {
                const columnsPos = $from.before(depth);

                node.forEach((child, childOffset) => {
                  if (child.type.name === 'column') {
                    const columnPos = columnsPos + 1 + childOffset;
                    decorations.push(
                      Decoration.node(columnPos, columnPos + child.nodeSize, {
                        class: 'editor-column--focused',
                      }),
                    );
                  }
                });
                break;
              }
            }

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
